// src/components/generic/MediaHero/MediaHero.tsx
//
// Reusable cover-media hero. Renders whatever combination of image + video the
// caller has:
//
//   image only        → the image.
//   video only        → the video with a play button (once it's ready).
//   image + video     → the image with a play button; tapping it slowly fades the
//                        image out and plays the video underneath.
//
// Presentation:
// - Media is shown CONTAINED (never cropped) so captions / text baked into the
//   image or video stay fully visible.
// - Behind it, the same image is used as a blurred, zoomed "aura" filling the
//   frame so a square/portrait asset doesn't leave dead black bars.
//
// Direct file video — buffering-aware playback (the whole point of this component):
// - The <video> is mounted immediately and starts BUFFERING in the background
//   (preload="auto") before the visitor presses anything, so play is instant.
// - Until the browser reports it can actually play, we show a LOADING RING (with
//   real buffered progress) instead of a dead play button — the play button only
//   appears when the video is ready to start.
// - If playback stalls mid-video (slow connection), a "Poor connection — buffering"
//   overlay makes it clear what's happening.
// - Cross-browser caveats handled: iOS/desktop Safari often won't buffer or emit
//   `canplay` before a user gesture, so we also reveal the play button on a metadata
//   + grace-period fallback (and a hard cap), respect `Save-Data`, and keep the tap
//   gesture that unlocks unmuted playback. No native controls.
// - YouTube / Vimeo keep their own player (their embeds can't be driven without
//   their JS SDKs) — the play-to-start + aura wrapper apply, with a spinner while
//   their iframe loads.
//
// Also supports a muted, controls-free BACKGROUND hero via `autoPlay`/`loop`/`muted`.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AppIcon } from "../AppIcon";

import styles from "./MediaHero.module.css";

export interface MediaHeroProps {
  /** Cover image URL (poster / standalone). */
  imageUrl?: string | null;
  /** Cover video URL — direct file, YouTube, or Vimeo. */
  videoUrl?: string | null;
  /** Alt text for the image. */
  alt?: string;
  /** CSS aspect-ratio, e.g. "16 / 9". Ignored when `fill` is set. Default "16 / 9". */
  aspectRatio?: string;
  /** Fill the parent (position/height come from the parent) instead of aspect-ratio. */
  fill?: boolean;
  /** Corner radius (px or CSS length). Default 0. */
  radius?: number | string;
  /** Start the video immediately, muted, with no play button (background hero). */
  autoPlay?: boolean;
  /** Loop the video (background hero). */
  loop?: boolean;
  /** Mute the video. Honored as-is: leave false with `autoPlay` to attempt unmuted
   *  autoplay (the browser may refuse it → the tap-to-play button is shown instead). */
  muted?: boolean;
  /** Above-the-fold hero: mark the cover image as the LCP (fetchPriority="high",
   *  eager) so the browser fetches it first. Leave off for below-the-fold uses. */
  priority?: boolean;
  /**
   * The caller starts the video from a gesture of ITS OWN (the event site's opening scene: the guest's tap opens it and
   * starts the video with sound). The hero puts its start here; the caller calls it INSIDE that gesture, or the browser
   * refuses the sound. Leave `autoPlay` off then — the start is the caller's.
   */
  startRef?: React.MutableRefObject<(() => void) | null>;
  className?: string;
  style?: React.CSSProperties;
}

interface VideoInfo {
  kind: "file" | "youtube" | "vimeo";
  embedUrl?: string;
  thumbUrl?: string;
}

const YT_RE = /(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([\w-]{11})/i;
const VIMEO_RE = /vimeo\.com\/(?:video\/)?(\d+)/i;
const VIDEO_FILE_RE = /\.(mp4|webm|ogg|ogv|mov|m4v)(\?|#|$)/i;

// Standard-player timings. The play button is always available (it pre-buffers in the
// background); tapping it shows a loading overlay with progress until the video actually
// starts advancing — robust across every browser and file, unlike trying to detect a
// "ready" moment before the tap.
const BUFFER_DEBOUNCE_MS = 250; // don't flash the overlay on a near-instant start
const SLOW_CONNECTION_MS = 2000; // after this much buffering, add a "slow connection" hint

function parseVideoUrl(url: string): VideoInfo | null {
  const trimmed = (url || "").trim();
  if (!trimmed) {
    return null;
  }

  const yt = trimmed.match(YT_RE);
  if (yt) {
    const id = yt[1];
    return {
      kind: "youtube",
      embedUrl: `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`,
      thumbUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    };
  }

  const vimeo = trimmed.match(VIMEO_RE);
  if (vimeo) {
    return { kind: "vimeo", embedUrl: `https://player.vimeo.com/video/${vimeo[1]}` };
  }

  if (VIDEO_FILE_RE.test(trimmed) || /^(blob:|data:video)/i.test(trimmed) || /^https?:/i.test(trimmed)) {
    return { kind: "file" };
  }

  return { kind: "file" };
}

const fillLayer: React.CSSProperties = { position: "absolute", inset: 0, width: "100%", height: "100%" };

// A HERO-SIZED loading overlay: a full-cover scrim (so it reads on any poster, light or
// dark) with a centred progress ring + buffered % and a short label. Determinate (an arc
// = buffered fraction, with the % shown) once we have a fraction, else an indeterminate
// spinner. Used for the initial buffer, mid-playback stalls, and embed loading — so the
// experience is consistent, and the wait always shows real progress.
const LoadingOverlay: React.FC<{ frac: number; label?: string; zIndex: number }> = ({ frac, label, zIndex }) => {
  const R = 34;
  const C = 2 * Math.PI * R;
  const clamped = Math.min(Math.max(frac, 0), 1);
  const determinate = frac > 0.02;
  const offset = determinate ? C * (1 - clamped) : C * 0.72;
  const pct = Math.round(clamped * 100);

  return (
    <div
      className={styles.appear}
      style={{
        position: "absolute",
        inset: 0,
        zIndex,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        // Radial scrim: darkest at the centre (behind the ring) fading out — legible on
        // any image without a boxy panel.
        background: "radial-gradient(120% 120% at 50% 46%, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 55%, rgba(0,0,0,0.2) 100%)",
        // Informational only — let taps reach the controls behind during a stall.
        pointerEvents: "none",
      }}
    >
      <div style={{ position: "relative", width: 88, height: 88 }}>
        <svg width={88} height={88} viewBox="0 0 88 88" className={determinate ? undefined : styles.spin} role="img" aria-label={label || "Loading"}>
          <circle cx={44} cy={44} r={R} fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth={4} />
          <circle
            cx={44}
            cy={44}
            r={R}
            fill="none"
            stroke="#fff"
            strokeWidth={4}
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={offset}
            transform="rotate(-90 44 44)"
            style={determinate ? { transition: "stroke-dashoffset 250ms linear", filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.4))" } : { filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.4))" }}
          />
        </svg>
        {/* Buffered % in the centre — only once we have a real fraction (never a
            misleading "0%" before any bytes arrive). */}
        {determinate && (
          <span
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 18,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              textShadow: "0 1px 4px rgba(0,0,0,0.6)",
            }}
          >
            {pct}%
          </span>
        )}
      </div>
      {label && (
        <span style={{ color: "#fff", fontSize: 14, fontWeight: 600, letterSpacing: 0.2, textShadow: "0 1px 4px rgba(0,0,0,0.6)" }}>
          {label}
        </span>
      )}
    </div>
  );
};

// A pill button used in the paused controls panel.
const ControlButton: React.FC<{ icon: string; label: string; onClick: () => void }> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 18px",
      borderRadius: 999,
      border: "1px solid rgba(255,255,255,0.35)",
      background: "rgba(0,0,0,0.6)",
      color: "#fff",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      backdropFilter: "blur(2px)",
    }}
  >
    <AppIcon iconName={icon} size={18} />
    {label}
  </button>
);

export const MediaHero: React.FC<MediaHeroProps> = ({
  imageUrl,
  videoUrl,
  alt = "",
  aspectRatio = "16 / 9",
  fill = false,
  radius = 0,
  autoPlay = false,
  loop = false,
  muted = false,
  priority = false,
  startRef,
  className,
  style,
}) => {
  const video = useMemo(() => (videoUrl ? parseVideoUrl(videoUrl) : null), [videoUrl]);
  const hasVideo = !!video;
  const poster = imageUrl || video?.thumbUrl || null;
  const isFileVideo = video?.kind === "file";
  // Respect the caller's `muted` exactly — we do NOT force mute on autoplay, so a host
  // can opt into unmuted autoplay (with the browser-may-block caveat). If the browser
  // refuses, `autoplayBlocked` reveals the tap-to-play button below.
  const forcedMute = muted;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [started, setStarted] = useState(autoPlay && hasVideo);
  const [paused, setPaused] = useState(false);
  const [isFs, setIsFs] = useState(false);

  // Playback / buffering state (file video).
  const [isPlaying, setIsPlaying] = useState(false); // the video is actively advancing
  const [bufferingVisible, setBufferingVisible] = useState(false); // debounced overlay
  const [bufferedFrac, setBufferedFrac] = useState(0);
  const [slowHint, setSlowHint] = useState(false);
  const [errored, setErrored] = useState(false);
  const [embedLoading, setEmbedLoading] = useState(false);
  // Autoplay (especially unmuted) can be refused by the browser until the visitor
  // interacts. When that happens we fall back to the tap-to-play button so the cover
  // is never stuck on a dead first frame.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  // Live mute state — starts from the caller's `muted` (true for a muted autoplay
  // background), but the visitor can toggle it with the speaker button (a muted video
  // with no way to hear it is pointless). Kept in state so the icon + element stay in sync.
  const [isMuted, setIsMuted] = useState(muted);

  // Reset when the media changes.
  useEffect(() => {
    setStarted(autoPlay && hasVideo);
    setPaused(false);
    setIsPlaying(false);
    setBufferingVisible(false);
    setBufferedFrac(0);
    setSlowHint(false);
    setErrored(false);
    setEmbedLoading(false);
    setAutoplayBlocked(false);
    setIsMuted(muted);
  }, [imageUrl, videoUrl, autoPlay, hasVideo, muted]);

  // Keep the <video> element's muted property in sync with our state (React can drop
  // the `muted` attribute on hydration, so set the property imperatively too).
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted, started]);

  // Autoplay is "effective" only until the browser refuses it — after that the hero
  // behaves like the standard tap-to-play player.
  const effectiveAuto = autoPlay && !autoplayBlocked;

  // "Buffering" = the user has started the video (not a muted background autoplay) but
  // it isn't advancing yet → show the loading overlay with progress.
  const buffering = isFileVideo && started && !effectiveAuto && !isPlaying && !paused && !errored;

  // Read how much is buffered (fraction of total duration).
  const updateBuffered = useCallback(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    const d = v.duration;
    if (!d || !Number.isFinite(d)) {
      return;
    }
    const b = v.buffered;
    const end = b.length ? b.end(b.length - 1) : 0;
    setBufferedFrac(Math.min(1, end / d));
  }, []);

  // Respect Save-Data: don't aggressively pre-buffer on metered/data-saver connections.
  // Set on the element directly (not via JSX) to avoid an SSR/CSR attribute mismatch.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    const conn = (navigator as unknown as { connection?: { saveData?: boolean } }).connection;
    if (conn?.saveData) {
      v.preload = "metadata";
    }
  }, [videoUrl]);

  // While buffering, poll the buffered fraction so the overlay's progress keeps moving
  // (some browsers fire `progress` sparsely).
  useEffect(() => {
    if (!buffering) {
      return;
    }
    const id = window.setInterval(updateBuffered, 400);
    updateBuffered();
    return () => window.clearInterval(id);
  }, [buffering, updateBuffered]);

  // Show the overlay only after a short debounce, so a near-instant start (fully buffered)
  // doesn't flash it.
  useEffect(() => {
    if (!buffering) {
      setBufferingVisible(false);
      return;
    }
    const t = window.setTimeout(() => setBufferingVisible(true), BUFFER_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [buffering]);

  // After buffering a while, add a "slow connection" hint.
  useEffect(() => {
    if (!buffering) {
      setSlowHint(false);
      return;
    }
    const t = window.setTimeout(() => setSlowHint(true), SLOW_CONNECTION_MS);
    return () => window.clearTimeout(t);
  }, [buffering]);

  // Kick off file playback once we've entered the started state.
  useEffect(() => {
    if (!(started && isFileVideo && videoRef.current)) {
      return;
    }
    const p = videoRef.current.play();
    if (p && typeof (p as Promise<void>).catch === "function") {
      (p as Promise<void>).catch(() => {
        // A refused AUTOPLAY (usually unmuted) → fall back to the tap-to-play button so
        // the guest can start it themselves. A refused manual start keeps the controls.
        if (autoPlay && !autoplayBlocked) {
          setAutoplayBlocked(true);
          setStarted(false);
        }
      });
    }
  }, [started, isFileVideo, autoPlay, autoplayBlocked]);

  // Track fullscreen state (for the enter/leave icon + sizing).
  useEffect(() => {
    const onChange = () => setIsFs(document.fullscreenElement === wrapperRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const startPlay = useCallback(() => {
    if (!isFileVideo) {
      setEmbedLoading(true);
      setStarted(true);
      return;
    }
    // Play SYNCHRONOUSLY inside the click gesture — deferring play() to an effect can lose
    // the user-activation on some browsers. The loading overlay covers the wait until it
    // actually advances.
    setStarted(true);
    videoRef.current?.play().catch(() => {
      /* gesture-gated — the tap controls remain usable */
    });
  }, [isFileVideo]);

  // Hand the start to a caller that starts it from its own gesture (`startRef`).
  useEffect(() => {
    if (!startRef || !hasVideo) {
      return undefined;
    }

    startRef.current = startPlay;

    return () => {
      startRef.current = null;
    };
  }, [startRef, hasVideo, startPlay]);

  const togglePlayback = useCallback(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    if (v.paused) {
      v.play().catch(() => undefined);
    } else {
      v.pause();
    }
  }, []);

  const resume = useCallback(() => videoRef.current?.play().catch(() => undefined), []);

  const startOver = useCallback(() => {
    const v = videoRef.current;
    if (!v) {
      return;
    }
    v.currentTime = 0;
    v.play().catch(() => undefined);
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    setIsMuted((m) => {
      const next = !m;
      if (v) {
        v.muted = next;
        // Unmuting counts as the user gesture that unlocks sound — make sure it's playing.
        if (!next) {
          v.play().catch(() => undefined);
        }
      }
      return next;
    });
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) {
      return;
    }
    if (document.fullscreenElement === el) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.().catch(() => undefined);
    }
  }, []);

  if (!poster && !hasVideo) {
    return null;
  }

  const isEmbed = hasVideo && !isFileVideo;
  // The play button is ALWAYS available once there's a video (it pre-buffers in the
  // background); tapping it shows the loading overlay until playback advances — so a tap
  // always does something visible, on every browser and file.
  const showPlayButton = hasVideo && !started && !effectiveAuto && !errored;
  // Controls (tap-to-pause / restart / mute / fullscreen) are available whenever a file
  // video has started — INCLUDING autoplay/looping backgrounds, so the visitor can always
  // stop it or turn the sound on.
  const showCustomControls = isFileVideo && started;
  const showBuffering = isFileVideo && bufferingVisible;

  const wrapperStyle: React.CSSProperties = {
    position: "relative",
    overflow: "hidden",
    // The surround uses the site/theme background (not black) so a contained or
    // portrait asset blends into the page; the blurred aura below adds a soft tint.
    backgroundColor: "var(--mantine-color-body)",
    // `fill` fills the (position:relative) parent frame by ABSOLUTE inset rather than
    // height:100%. Safari won't resolve a percentage height against a parent sized with
    // `calc(100svh - …)` (our full-height hero), so height:100% collapses there and the
    // media vanishes — while Chrome/Firefox resolve it. Absolute inset works everywhere.
    // Fullscreen keeps its own explicit viewport sizing.
    ...(isFs
      ? { width: "100vw", height: "100vh", borderRadius: 0 }
      : fill
        ? { position: "absolute", inset: 0, borderRadius: radius }
        : { width: "100%", aspectRatio, borderRadius: radius }),
    ...style,
  };

  return (
    <div ref={wrapperRef} className={className} style={wrapperStyle}>
      {/* 1. Blurred, zoomed aura so contained media doesn't sit on dead black. */}
      {poster && (
        <img
          src={poster}
          alt=""
          aria-hidden
          draggable={false}
          fetchPriority={priority ? "high" : undefined}
          loading={priority ? "eager" : undefined}
          style={{
            ...fillLayer,
            objectFit: "cover",
            transform: "scale(1.2)",
            // Translucent + blurred so it reads as a soft tint over the theme background
            // rather than a hard dark band (no forced darkening → it matches light/dark).
            opacity: 0.55,
            filter: "blur(30px) saturate(1.2)",
          }}
        />
      )}

      {/* 2. File video — mounted immediately so it buffers in the background; contained,
             no native controls. Hidden behind the poster until it starts. */}
      {isFileVideo && (
        <video
          ref={videoRef}
          src={videoUrl || undefined}
          poster={poster || undefined}
          controls={false}
          muted={isMuted}
          loop={loop}
          autoPlay={autoPlay}
          playsInline
          preload="auto"
          onLoadedMetadata={updateBuffered}
          onLoadedData={updateBuffered}
          onProgress={updateBuffered}
          // `playing`/`timeupdate` mean it's actually advancing → hide the loading overlay.
          // `waiting` means it stalled → show it again.
          onPlaying={() => {
            setIsPlaying(true);
            setPaused(false);
          }}
          onTimeUpdate={() => {
            setIsPlaying(true);
            updateBuffered();
          }}
          onWaiting={() => setIsPlaying(false)}
          onError={() => setErrored(true)}
          onPlay={() => setPaused(false)}
          onPause={() => {
            setPaused(true);
            setIsPlaying(false);
          }}
          onEnded={() => {
            setPaused(true);
            setIsPlaying(false);
          }}
          style={{ ...fillLayer, objectFit: "contain", opacity: started ? 1 : 0, transition: "opacity 400ms ease" }}
        />
      )}

      {/* YouTube / Vimeo keep their own player (loaded only on play). */}
      {isEmbed && started && (
        <iframe
          src={`${video?.embedUrl}${video?.embedUrl?.includes("?") ? "&" : "?"}autoplay=1${forcedMute ? "&mute=1" : ""}`}
          title={alt || "video"}
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          onLoad={() => setEmbedLoading(false)}
          style={{ ...fillLayer, border: 0 }}
        />
      )}

      {/* 3. Foreground poster — contained, slow fade-out when the video starts. */}
      {poster && (
        <img
          src={poster}
          alt={alt}
          draggable={false}
          fetchPriority={priority ? "high" : undefined}
          loading={priority ? "eager" : undefined}
          style={{
            ...fillLayer,
            objectFit: "contain",
            opacity: hasVideo && started ? 0 : 1,
            transition: "opacity 900ms ease",
            pointerEvents: "none",
          }}
        />
      )}

      {/* 4b. Play button — always available; tapping shows the loading overlay below. */}
      {showPlayButton && (
        <button
          type="button"
          onClick={startPlay}
          aria-label="Play video"
          className={styles.appear}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: 0,
            background: "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.25))",
            cursor: "pointer",
            padding: 0,
            zIndex: 3,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 68,
              height: 68,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.5)",
              border: "2px solid rgba(255,255,255,0.9)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
              backdropFilter: "blur(2px)",
            }}
          >
            <span style={{ marginLeft: 4, color: "#fff", display: "flex" }}>
              <AppIcon iconName="PlayerPlay" size={30} />
            </span>
          </span>
        </button>
      )}

      {/* 4c. Embed (YouTube/Vimeo) — spinner while its iframe loads (no buffered % to show). */}
      {isEmbed && started && embedLoading && <LoadingOverlay frac={0} label="Loading…" zIndex={2} />}

      {/* 4d. Loading overlay — from the moment play is tapped until the video advances,
             AND on any mid-playback stall. Shows the buffered % where the browser reports
             it (a non-faststart file reports none until fully fetched → a spinner). */}
      {showBuffering && <LoadingOverlay frac={bufferedFrac} label={slowHint ? "Loading — slow connection…" : "Loading…"} zIndex={4} />}

      {/* 4e. Load error — fall back to the poster (already shown) with a quiet note. */}
      {errored && (
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 10, textAlign: "center", zIndex: 5, pointerEvents: "none" }}>
          <span style={{ background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 12, fontWeight: 600, padding: "4px 12px", borderRadius: 999 }}>
            Couldn&rsquo;t load the video
          </span>
        </div>
      )}

      {/* 4f. Custom controls for file video: tap to pause, then Start over / Resume + fullscreen. */}
      {showCustomControls && (
        <div
          onClick={togglePlayback}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            cursor: "pointer",
            background: paused ? "rgba(0,0,0,0.4)" : "transparent",
            transition: "background 250ms ease",
          }}
        >
          {paused && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                flexWrap: "wrap",
                padding: 16,
              }}
            >
              <ControlButton icon="Refresh" label="Start over" onClick={startOver} />
              <ControlButton icon="PlayerPlay" label="Resume" onClick={resume} />
            </div>
          )}

          <div style={{ position: "absolute", bottom: 10, right: 10, display: "flex", gap: 8 }}>
            {/* Mute / unmute — so a muted (background/autoplay) video can be heard. */}
            <button
              type="button"
              aria-label={isMuted ? "Unmute" : "Mute"}
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                border: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                cursor: "pointer",
                backdropFilter: "blur(2px)",
              }}
            >
              <AppIcon iconName={isMuted ? "VolumeOff" : "Volume"} size={20} />
            </button>
            <button
              type="button"
              aria-label={isFs ? "Leave fullscreen" : "Fullscreen"}
              onClick={(e) => {
                e.stopPropagation();
                toggleFullscreen();
              }}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                border: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.55)",
                color: "#fff",
                cursor: "pointer",
                backdropFilter: "blur(2px)",
              }}
            >
              <AppIcon iconName={isFs ? "Minimize" : "Maximize"} size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
